import { useNativePreferences } from '/src/native/useNativePreferences.js';

/**
 * NativePreferencesAdapter - Adapts useNativePreferences to persist plugin contract
 *
 * This adapter translates the persist plugin's contract (get, set, remove, clear)
 * to the Capacitor Preferences API provided by useNativePreferences composable.
 *
 * Capacitor Preferences storage:
 * - Native: UserDefaults (iOS), SharedPreferences (Android)
 * - Web: localStorage
 * - Capacity: ~50-100 MB (much larger than localStorage's 1-2 MB)
 *
 * @returns {Object} Persist plugin storage adapter
 */
export function createNativePreferencesAdapter () {
	const preferences = useNativePreferences();

	return {
		/**
         * Get a value by key (persist plugin contract)
         * @param {Object} params
         * @param {string} params.key - The key to retrieve
         * @returns {Promise<{value: string}>} Object with value property
         * @throws {Error} If key not found
         */
		async get ({ key }) {
			const result = await preferences.get({ key });

			if (result.value === null) {
				throw new Error(`Key "${key}" not found in Capacitor Preferences`);
			}

			return { value: result.value };
		},

		/**
         * Set a value by key (persist plugin contract)
         * @param {Object} params
         * @param {string} params.key - The key to set
         * @param {string} params.value - The value to store (already serialized)
         * @returns {Promise<void>}
         */
		async set ({ key, value }) {
			await preferences.set({ key, value });
		},

		/**
         * Remove a value by key (persist plugin contract)
         * @param {Object} params
         * @param {string} params.key - The key to remove
         * @returns {Promise<void>}
         */
		async remove ({ key }) {
			await preferences.remove({ key });
		},

		/**
         * Clear all values (persist plugin contract)
         * @returns {Promise<void>}
         */
		async clear () {
			await preferences.clear();
		},
	};
}
